import AuthFieldErrorMessage from '@/components/Auth/AuthFieldErrorMessage'
import { ColorEnum } from '@/constants/color'
import { isWeb } from '@/constants/environment'
import { FontEnum } from '@/constants/font'
import { logger } from '@/features/auth/logger'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { fetchAuthDeviceId, lookupAuthLicence, setAuthLicenceId } from '@/redux/reducers/auth'
import { authSelectors } from '@/redux/reducers/auth/selectors'
import { isThunkRejectValue } from '@/redux/utils'
import { getAuthLicenceErrorTranslation } from '@/utils/auth/translation'
import { isNumeric } from '@/utils/isNumeric'
import { router } from 'expo-router'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, TextInput, View } from 'react-native'

const AuthLicenceIdField: FC = () => {
    const licenceId = useAppSelector(authSelectors.licenceId)

    const dispatch = useAppDispatch()

    const [isHovered, setIsHovered] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string>()

    const { t } = useTranslation()

    const handleChangeText = (licenceId: string) => {
        if (loading) return

        if (!isNumeric(licenceId)) return

        dispatch(setAuthLicenceId(licenceId))

        if (errorMessage) setErrorMessage(undefined)
    }

    const handleMouseEnter = () => {
        if (loading) return

        setIsHovered(true)
    }

    const handleMouseLeave = () => {
        setIsHovered(false)
    }

    const handleFocus = () => {
        if (loading) return

        setIsFocused(true)
    }

    const handleBlur = () => {
        setIsFocused(false)
    }

    const handleSubmitEditing = async () => {
        if (licenceId.length === 0 || loading) return

        setLoading(true)
        if (isWeb) handleMouseLeave()
        handleBlur()

        try {
            await dispatch(lookupAuthLicence()).unwrap()
            await dispatch(fetchAuthDeviceId()).unwrap()
        } catch (e) {
            let errorMessage: string

            if (isThunkRejectValue(e)) {
                errorMessage = t(getAuthLicenceErrorTranslation(e))
            } else {
                logger.errorOnly(e)

                errorMessage = t('unexpectedError')
            }

            setErrorMessage(errorMessage)

            return setLoading(false)
        }

        router.replace('/auth/device')
    }

    return (
        <View style={styles.container}>
            <TextInput
                autoFocus
                editable={!loading}
                cursorColor={ColorEnum.BLUE_400}
                selectionColor={ColorEnum.BLUE_400}
                inputMode="numeric"
                returnKeyType="done"
                placeholder={t('licenceVerificationScreenLicenceId')}
                placeholderTextColor={ColorEnum.GRAY_300}
                value={licenceId}
                onChangeText={handleChangeText}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onSubmitEditing={handleSubmitEditing}
                {...(isWeb && {
                    onMouseEnter: handleMouseEnter,
                    onMouseLeave: handleMouseLeave,
                })}
                style={[
                    styles.field,
                    isHovered && styles.hovered,
                    isFocused && styles.focused,
                    loading && styles.disabled,
                    !!errorMessage && styles.error,
                ]}
            />
            {errorMessage && <AuthFieldErrorMessage>{errorMessage}</AuthFieldErrorMessage>}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        marginTop: 64,
        gap: 16,
    },
    field: {
        backgroundColor: ColorEnum.WHITE,
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 8,
        borderWidth: 1,
        color: ColorEnum.JET_900,
        fontFamily: FontEnum.SatoshiRegular,
        fontSize: 14,
        height: 48,
        width: 240,
        paddingHorizontal: 16,
        ...(isWeb && {
            caretColor: ColorEnum.BLUE_400,
            outlineStyle: 'none',
        }),
    },
    hovered: {
        borderColor: ColorEnum.BRICK_700,
    },
    focused: {
        borderColor: ColorEnum.BRICK_700,
        elevation: 4,
        shadowColor: ColorEnum.BRICK_700,
        shadowOffset: {
            height: 0,
            width: 0,
        },
        shadowOpacity: 1,
        shadowRadius: 4,
    },
    disabled: {
        backgroundColor: ColorEnum.GRAY_200,
        color: ColorEnum.GRAY_400,
    },
    error: {
        borderColor: ColorEnum.RED_600,
    },
})

export default AuthLicenceIdField
